import Sentry from '@/services/sentry'
import ImportStatus from '@/constant/importStatus'
import eventCategories from '@/constant/eventCategories'
import utils from '@/util'
import EventBus from '@/eventBus'
import router from '@/router'
import getters from '@/vuex/getters'
import Socket from '@/socket'

// api
import UploadApi from '@/api/upload'

import { IMPORT_DOCUMENT } from '@/constant/eventActions'
import { EVENT_CATEGORIES_BY_ROUTE_NAME } from '@/constant/routes'
import { ASSISTANT_PANEL_SOURCES } from '@/utils/assistantTracking'

const SET_DOC_TO_SLIDE_IMPORT_STAGE = 'SET_DOC_TO_SLIDE_IMPORT_STAGE'
const SET_IS_DOC_TO_SLIDE_IMPORT_MODAL_VISIBLE = 'SET_IS_DOC_TO_SLIDE_IMPORT_MODAL_VISIBLE'
const SET_DOC_TO_SLIDE_RESPONSE_ERROR = 'SET_DOC_TO_SLIDE_RESPONSE_ERROR'
const SET_DOC_TO_SLIDE_UNIQUE_REQUEST_ID = 'SET_DOC_TO_SLIDE_UNIQUE_REQUEST_ID'
const SET_DOC_TO_SLIDE_RESULT_MODAL_VISIBLE = 'SET_DOC_TO_SLIDE_RESULT_MODAL_VISIBLE'
const SET_DOC_TO_SLIDE_RESPONSE = 'SET_DOC_TO_SLIDE_RESPONSE'
const SET_IS_DONE_GENERATING = 'SET_IS_DONE_GENERATING'
const SET_IS_RESPONSE_WITH_ERROR = 'SET_IS_RESPONSE_WITH_ERROR'
const SET_IS_SUBMITTING_FEEDBACK = 'SET_IS_SUBMITTING_FEEDBACK'
const SET_CONTENT_LANGUAGE = 'SET_CONTENT_LANGUAGE'
const SET_NUMBER_OF_GENERATED_SLIDES = 'SET_NUMBER_OF_GENERATED_SLIDES'
const SET_IS_LOADING_SPINNER_HIDDEN = 'SET_IS_LOADING_SPINNER_HIDDEN'
const SET_CUSTOM_AI_STUMPED_TEXT = 'SET_CUSTOM_AI_STUMPED_TEXT'
const SET_AI_CONVERSATION_MESSAGE_ACTION = 'aiConversation/removeSystemMessageAndAddSuccessErrorMessageToConversationAction'
const IS_EU_REGION = getters.isEURegion()

// TRACKING FUNCTIONS
const _getFailureReason = (state, error) => {
  if (error) {
    return error
  }
  if (state.docToSlideImportStage === ImportStatus.ERROR_REACH_LIMIT) {
    return 'Reach limit of 50MB or 100 slides'
  }
  if (state.docToSlideImportStage === ImportStatus.ERROR_UNSUPPORTED_FILE_TYPE) {
    return 'Unsupported file type'
  }
  return 'Unknown'
}

const trackDocToSlideError = (payloadError, state, rootState) => {
  if (IS_EU_REGION) return
  const error = payloadError && payloadError.content
  const failureReason = _getFailureReason(state, error)
  const eventAction = `${IMPORT_DOCUMENT} > Failure`
  const trackData = {
    eventAction,
    eventCategory: eventCategories.PRESENTATION_EDITOR,
    isGenerateQuizFromThisDocument: true,
    failureReason,
    presentationId: rootState.presentation.id,
    userId: rootState.currentUser.id,
  }
  utils.trackGA4AndMixpanel(eventAction, trackData)
}

const trackFirstAIResultDisplayed = (state, rootState) => {
  if (IS_EU_REGION) return
  if (state.numberOfGeneratedSlides > 0) {
    return
  }
  const eventAction = `${IMPORT_DOCUMENT} > First result displayed`
  const trackData = {
    eventAction,
    eventCategory: EVENT_CATEGORIES_BY_ROUTE_NAME[router.currentRoute.name],
    eventLabel: 'Generate quiz from doc',
    presentationId: rootState.presentation.id,
    userId: rootState.currentUser.id,
  }
  utils.sendCustomEventToGTM(eventAction, trackData)
}

const trackDocToSlideGenerationDone = (rootState) => {
  if (IS_EU_REGION) return
  const eventAction = `${IMPORT_DOCUMENT} > Quiz generation success`
  const trackData = {
    eventAction,
    eventCategory: eventCategories.PRESENTATION_EDITOR,
    eventLabel: 'Generate quiz from doc',
    presentationId: rootState.presentation.id || null,
    userId: rootState.currentUser.id,
  }
  utils.trackGA4AndMixpanel(eventAction, trackData)
}

// OTHER FUNCTIONS
const showErrorToaster = (socketData, rootState) => {
  try {
    const errorMessage = socketData && socketData.message
    const isImportingDocument = rootState.importStatus == ImportStatus.IMPORT_PPT_UPLOADING
      || rootState.importStatus == ImportStatus.IMPORT_PPT_CONVERTING
    if (!isImportingDocument) return
    EventBus.$emit('doc-to-slide: show_toast_error', errorMessage)
  } catch (error) {
    Sentry.captureException(error)
  }
}

const handleDocToSlideSocketError = (commit, dispatch, state, socketData) => {
  try {
    if (socketData.error && !socketData.message) {
      commit(SET_IS_DONE_GENERATING, true)
    }
    if (socketData.error && socketData.message) {
      commit(SET_IS_DONE_GENERATING, false)
      commit(SET_DOC_TO_SLIDE_RESPONSE_ERROR, socketData.message)
    }
    commit(SET_IS_RESPONSE_WITH_ERROR, true)
    if (!state.docToSlideResponse.length) {
      dispatch('handleDocToSlideErrorAction', { content: 'AI stumped', isAPIError: true })
    }
  } catch (error) {
    Sentry.captureException(error)
  }
}

const handleDocToSlideSocketSuccess = (commit, dispatch, state, rootState, socketData) => {
  try {
    if (!state.isDocToSlideDoneGenerating) {
      commit(SET_IS_SUBMITTING_FEEDBACK, false)
      commit(SET_IS_RESPONSE_WITH_ERROR, false)
    }
    const hasResponses = socketData && socketData.responses && socketData.responses.type
    if (!hasResponses) return

    dispatch('setDocToSlideResponseAction', [...state.docToSlideResponse, socketData.responses])
    if (!state.docToSlideContentLanguage) commit(SET_CONTENT_LANGUAGE, socketData.language)

    trackFirstAIResultDisplayed(state, rootState)
    commit(SET_NUMBER_OF_GENERATED_SLIDES, state.numberOfGeneratedSlides + 1)
  } catch (error) {
    Sentry.captureException(error)
  }
}

const scrollToBottom = async (dispatch) => {
  dispatch('progressBar/incrementProgressBarAction', null, { root: true })
  await utils.sleep(500)
  const element = document.getElementById('doc-to-quiz__content-wrapper')
  if (!element) return
  element.scrollTo({
    top: element.scrollHeight + 200,
    behavior: 'smooth',
  })
}

const handleDocToSlideGlobalImportSuccess = (commit, state, rootState) => {
  try {
    commit(SET_IS_DONE_GENERATING, true)
    if (state.isDocToSlideImportModalVisible) commit(SET_IS_DOC_TO_SLIDE_IMPORT_MODAL_VISIBLE, false)
    if (state.docToSlideUniqueRequestId) commit(SET_DOC_TO_SLIDE_IMPORT_STAGE, ImportStatus.NO_IMPORT)
    EventBus.$emit('doc-to-quiz: close_generating_toast', true)
    trackDocToSlideGenerationDone(rootState)
  } catch (error) {
    Sentry.captureException(error)
  }
}

const setFormDataUploadPptPdfForAI = (rootState, dispatch, file) => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('socketId', Socket.getSocketId())
  formData.append('uniqueAccessCode', rootState.presentation.uniqueAccessCode)
  formData.append('type', 'importPpt')
  formData.append('fileSize', file.size)
  formData.append('fileName', file.name)
  if (!rootState.docToSlideGenerator.isDocToSlideGenerating) {
    formData.append('needGenerateQuiz', true)

    const requestId = utils.generateRandomString()
    dispatch('generateDocToSlideUniqueRequestIdAction', `${requestId}-${rootState.presentation.id}`)
    formData.append('requestId', rootState.docToSlideGenerator.docToSlideUniqueRequestId)

    dispatch('setDocToSlideImportStageAction', ImportStatus.UPLOADING)
    EventBus.$emit('doc-to-quiz: reset_all_results')
  }
  if (rootState.presentation.id) {
    formData.append('presentationId', rootState.presentation.id)
  }
  const headers = new Headers()
  headers.append('Content-Type', 'application/x-www-form-urlencoded')
  return formData
}

const trackBeginUploadFileImportLegacy = (rootState) => {
  const eventAction = `${IMPORT_DOCUMENT} > Begin uploading`
  const trackData = {
    eventAction,
    eventCategory: EVENT_CATEGORIES_BY_ROUTE_NAME[router.currentRoute.name],
    isGenerateQuizFromThisDocument: true,
    presentationId: rootState.presentation.id,
    userId: rootState.currentUser.id,
  }
  utils.trackGA4AndMixpanel(eventAction, trackData)
}

const _state = {
  docToSlideImportStage: ImportStatus.NO_IMPORT,
  isDocToSlideImportModalVisible: false,
  docToSlideResponseError: null,
  docToSlideUniqueRequestId: null,
  isDocToSlideResultModalVisible: false,
  docToSlideResponse: [],
  isDocToSlideDoneGenerating: false,
  isDocToSlideResponseWithError: false,
  isDocToSlideSubmittingFeedback: false,
  docToSlideContentLanguage: null,
  numberOfGeneratedSlides: 0,
  isDocToSlideLoadingSpinnerHidden: false,
  docToSlideCustomAIStumpedText: [],
}

const _getters = {
  docToSlideImportStage(state) {
    return state.docToSlideImportStage
  },
  isDocToSlideImportModalVisible(state) {
    return state.isDocToSlideImportModalVisible
  },
  docToSlideUniqueRequestId(state) {
    return state.docToSlideUniqueRequestId
  },
  docToSlideResponseError(state) {
    return state.docToSlideResponseError
  },
  isDocToSlideResultModalVisible(state) {
    return state.isDocToSlideResultModalVisible
  },
  docToSlideResponse(state) {
    return state.docToSlideResponse
  },
  isDocToSlideGenerating(state) {
    return state.docToSlideUniqueRequestId &&
      state.docToSlideUniqueRequestId.length > 0
  },
  isDocToSlideDoneGenerating(state) {
    return state.isDocToSlideDoneGenerating
  },
  docToSlideContentLanguage(state) {
    return state.docToSlideContentLanguage
  },
  isDocToSlideSubmittingFeedback(state) {
    return state.isDocToSlideSubmittingFeedback
  },
  isDocToSlideLoadingSpinnerHidden(state) {
    return state.isDocToSlideLoadingSpinnerHidden
  },
  docToSlideCustomAIStumpedText(state) {
    return state.docToSlideCustomAIStumpedText
  },
  isDocToSlideResponseWithError(state) {
    return state.isDocToSlideResponseWithError
  },
}

const actions = {
  setDocToSlideImportStageAction({ commit }, status) {
    commit(SET_DOC_TO_SLIDE_IMPORT_STAGE, status)
  },
  toggleDocToSlideImportModalAction({ commit }, value) {
    commit(SET_DOC_TO_SLIDE_RESPONSE_ERROR, null)
    commit(SET_IS_DOC_TO_SLIDE_IMPORT_MODAL_VISIBLE, value)
  },
  generateDocToSlideUniqueRequestIdAction({ commit }, value) {
    commit(SET_DOC_TO_SLIDE_UNIQUE_REQUEST_ID, value)
  },
  setDocToSlideResponseErrorAction({ commit }, value) {
    commit(SET_DOC_TO_SLIDE_RESPONSE_ERROR, value)
  },
  toggleDocToSlideResultModalAction({ commit }, value) {
    commit(SET_DOC_TO_SLIDE_RESULT_MODAL_VISIBLE, value)
  },
  setDocToSlideResponseAction({ commit }, value) {
    commit(SET_DOC_TO_SLIDE_RESPONSE, value)
  },
  resetIsDocToSlideGenerationDoneAction({ commit }) {
    commit(SET_IS_DONE_GENERATING, false)
  },
  setLoadingButtonAddSlideAction({ commit, state }, payload) {
    let { response } = payload
    const { value } = payload
    response = state.docToSlideResponse.map((res) => {
      return {
        ...res,
        isDisabled: value,
        isAddingSlide: response.id === res.id ? value : false,
      }
    })
    commit(SET_DOC_TO_SLIDE_RESPONSE, response)
  },
  resetDocToSlideRequestIdAction({ commit }) {
    commit(SET_IS_DONE_GENERATING, false)
  },
  handleDocToSlideErrorAction({
    commit, dispatch, state, rootState, rootGetters,
  }, payloadError) {
    try {
      EventBus.$emit('doc-to-quiz: close_generating_toast', true)
      commit(SET_DOC_TO_SLIDE_UNIQUE_REQUEST_ID, null)
      commit(SET_DOC_TO_SLIDE_IMPORT_STAGE, ImportStatus.NO_IMPORT)
      if (payloadError && payloadError.isAPIError) {
        commit(SET_IS_DOC_TO_SLIDE_IMPORT_MODAL_VISIBLE, false)
      }
      if (!rootGetters['aiChatbot/canUseMasterAgentOnly']) {
        dispatch(SET_AI_CONVERSATION_MESSAGE_ACTION, { mode: 'error' }, { root: true })
      }
      trackDocToSlideError(payloadError, state, rootState)
    } catch (error) {
      Sentry.captureException(error)
    }
  },
  handleDocToSlideSocketAction({
    commit, dispatch, state, rootState,
  }, socketData) {
    try {
      if (socketData.error) {
        handleDocToSlideSocketError(commit, dispatch, state, socketData)
        showErrorToaster(socketData, rootState)
        return
      }
      const hasRequestId = socketData && socketData.requestId && socketData.requestId.length > 0
      const generatedPresentationId = hasRequestId && Number(socketData.requestId.split('-')[1])
      if (socketData.requestId === state.docToSlideUniqueRequestId &&
        rootState.presentation.id === generatedPresentationId) {
        handleDocToSlideSocketSuccess(commit, dispatch, state, rootState, socketData)
      }
    } catch (error) {
      Sentry.captureException(error)
    }
  },

  async handleDocToSlideSocketGlobalImportAction({
    commit, dispatch, state, rootState, rootGetters,
  }, socketData) {
    try {
      if (!socketData) return
      await dispatch('rightNav/openAIPanelAction', { source: ASSISTANT_PANEL_SOURCES.DOC_TO_SLIDE }, { root: true })
      if (socketData.error) {
        EventBus.$emit('doc-to-slide: show_toast_error')
        dispatch('handleDocToSlideErrorAction', { isAPIError: true })
      } else if (socketData.data) {
        commit(SET_DOC_TO_SLIDE_RESPONSE, socketData.data)
      }
      if (!state.docToSlideUniqueRequestId) return
      if (!state.docToSlideResponse.length) {
        dispatch('handleDocToSlideErrorAction', { content: 'Cannot read the document', isAPIError: true })
      } else {
        handleDocToSlideGlobalImportSuccess(commit, state, rootState)
        if (!rootGetters['aiChatbot/canUseMasterAgentOnly']) {
          dispatch(SET_AI_CONVERSATION_MESSAGE_ACTION, { mode: 'success' }, { root: true })
        }
      }
      rootState.docToQuizNewPresentationRequestId = null
      commit(SET_DOC_TO_SLIDE_IMPORT_STAGE, ImportStatus.NO_IMPORT)
    } catch (error) {
      Sentry.captureException(error)
    }
  },
  handleDocToSlideSocketImportConvertErrorAction({ commit, state }, socketData) {
    try {
      if (socketData.requestId !== state.docToSlideUniqueRequestId) return
      commit(SET_DOC_TO_SLIDE_RESPONSE_ERROR, 'Import failed.')
    } catch (error) {
      Sentry.captureException(error)
    }
  },
  docToSlideGenerateSlidesFromFeedbackAction({
    commit, dispatch, state, rootState,
  }, feedback) {
    try {
      commit(SET_IS_SUBMITTING_FEEDBACK, true)
      scrollToBottom(dispatch)
      const requestId = `${utils.generateRandomString()}-${rootState.presentation.id}`
      dispatch('generateDocToSlideUniqueRequestIdAction', requestId)

      const { aiRequestId } = state.docToSlideResponse[0]
      const allQuizzes = state.docToSlideResponse.map((quiz) => {
        return quiz.data[0].questionTitle
      })

      const payload = {
        feedbackMessage: feedback,
        uniqueAccessCode: rootState.presentation.uniqueAccessCode,
        requestId,
        aiRequestId,
        allQuizzes,
        language: state.docToSlideContentLanguage,
      }
      dispatch('slidesGeneratorFeedback/sendFeedbackOnDocumentQuizzes', payload, { root: true })
    } catch (error) {
      Sentry.captureException(error)
      commit(SET_IS_SUBMITTING_FEEDBACK, false)
      commit(SET_IS_RESPONSE_WITH_ERROR, true)
      commit(SET_IS_LOADING_SPINNER_HIDDEN, true)
      commit(SET_CUSTOM_AI_STUMPED_TEXT, ["Can't generate quiz at the moment."])
      dispatch('handleDocToSlideErrorAction', { content: 'Error while sending feedback', isAPIError: true })
      commit(SET_IS_DOC_TO_SLIDE_IMPORT_MODAL_VISIBLE, false)
      commit(SET_DOC_TO_SLIDE_IMPORT_STAGE, ImportStatus.NO_IMPORT)
    }
  },

  async docToSlideUploadFileAction({ commit, dispatch, rootState }, file) {
    try {
      const formData = setFormDataUploadPptPdfForAI(rootState, dispatch, file)
      dispatch('setImportStatusAction', ImportStatus.IMPORT_PPT_UPLOADING, { root: true })
      trackBeginUploadFileImportLegacy(rootState)
      await UploadApi.uploadPptPdf(formData)

      dispatch('resetIsDocToSlideGenerationDoneAction')
      commit(SET_DOC_TO_SLIDE_IMPORT_STAGE, ImportStatus.CONVERTING)
    } catch (error) {
      dispatch('toggleDocToSlideImportModalAction', false)
      commit(SET_DOC_TO_SLIDE_IMPORT_STAGE, ImportStatus.NO_IMPORT)
      dispatch('handleDocToSlideErrorAction', null)
      Sentry.captureException(error)
    } finally {
      dispatch('setImportStatusAction', ImportStatus.IMPORT_PPT_CONVERTING, { root: true })
    }
  },
}

const mutations = {
  [SET_DOC_TO_SLIDE_IMPORT_STAGE](state, status) {
    state.docToSlideImportStage = status
  },
  [SET_IS_DOC_TO_SLIDE_IMPORT_MODAL_VISIBLE](state, value) {
    state.isDocToSlideImportModalVisible = value
  },
  [SET_DOC_TO_SLIDE_RESPONSE_ERROR](state, value) {
    state.docToSlideResponseError = value
  },
  [SET_DOC_TO_SLIDE_UNIQUE_REQUEST_ID](state, value) {
    state.docToSlideUniqueRequestId = value
  },
  [SET_DOC_TO_SLIDE_RESULT_MODAL_VISIBLE](state, value) {
    state.isDocToSlideResultModalVisible = value
  },
  [SET_DOC_TO_SLIDE_RESPONSE](state, value) {
    state.docToSlideResponse = value
  },
  [SET_IS_DONE_GENERATING](state, value) {
    state.isDocToSlideDoneGenerating = value
  },
  [SET_IS_RESPONSE_WITH_ERROR](state, value) {
    state.isDocToSlideResponseWithError = value
  },
  [SET_IS_SUBMITTING_FEEDBACK](state, value) {
    state.isDocToSlideSubmittingFeedback = value
  },
  [SET_CONTENT_LANGUAGE](state, value) {
    state.docToSlideContentLanguage = value
  },
  [SET_NUMBER_OF_GENERATED_SLIDES](state, value) {
    state.numberOfGeneratedSlides = value
  },
  [SET_IS_LOADING_SPINNER_HIDDEN](state, value) {
    state.isDocToSlideLoadingSpinnerHidden = value
  },
  [SET_CUSTOM_AI_STUMPED_TEXT](state, value) {
    state.docToSlideCustomAIStumpedText = value
  },
}

const openAI = {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}

export default openAI
